import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Snackbar,
  SnackbarContent
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { useTypedSelector } from '../../reducers'
import {
  getUserTenants as apiGetUserTenants,
  listProjects as apiListProjects,
  listFolders as apiListFolders,
  listFoldersRecursive as apiListFoldersRecursive,
  copyDataset as apiCopyDataset,
  getTransferStatus as apiGetTransferStatus,
  httpStatusOf,
  messageOf
} from '../../apis/datasetTransfer'
import {
  getCachedTenantAccessToken,
  isTokenUsableForTenant,
  clearTenantAccessToken,
  cacheTenantAccessToken
} from '../../actions/tokenBridge'

const StyledAuthNotice = styled('div')(({ theme }) => ({
  background: theme.palette.warning.light,
  color: theme.palette.warning.contrastText,
  borderRadius: 6,
  padding: theme.spacing(1),
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  marginTop: theme.spacing(1)
}))

const StyledSnackInfo = styled(SnackbarContent)(({ theme }) => ({
  background: theme.palette.info.main,
  color: theme.palette.info.contrastText
}))

const StyledSnackSuccess = styled(SnackbarContent)(({ theme }) => ({
  background: theme.palette.success.main,
  color: theme.palette.success.contrastText
}))

const StyledSnackError = styled(SnackbarContent)(({ theme }) => ({
  background: theme.palette.error.main,
  color: theme.palette.error.contrastText
}))

const StyledSnackMsg = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}))

type Tenant = { id: string; name: string; role?: string }
type Project = { id: string; name: string }
type Folder = { id: string; name: string }

type Props = {
  open: boolean
  datasetId: string
  onClose: () => void
  onCopied?: () => void
}

const toItems = (resp: any) =>
  Array.isArray(resp) ? resp : resp?.data ?? resp?.items ?? []

const withTimeout = async <T,>(p: Promise<T>, ms = 12000): Promise<T> => {
  let t: number | undefined
  const timeout = new Promise<never>((_, reject) => {
    t = window.setTimeout(() => reject(new Error('Request timed out')), ms)
  })
  try {
    return await Promise.race([p, timeout])
  } finally {
    if (t) window.clearTimeout(t)
  }
}

export default function CopyDatasetDialog({
  open,
  datasetId,
  onClose,
  onCopied
}: Props) {
  const tenantsFromStore = useTypedSelector(s => s.tenant.tenants) as
    | Tenant[]
    | undefined
  const currentTenantId = useTypedSelector(s => s.auth?.user?.tenantId) ?? ''

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [projects, setProjects] = useState<Project[]>([])
  const [folders, setFolders] = useState<Folder[]>([])
  const [nestedFolders, setNestedFolders] = useState<Folder[]>([])
  const [tenantId, setTenantId] = useState('')
  const [projectId, setProjectId] = useState('')
  const [folderId, setFolderId] = useState('')
  const [nestedFolderId, setNestedFolderId] = useState('')

  const [loadingTenants, setLoadingTenants] = useState(false)
  const [loadingProjects, setLoadingProjects] = useState(false)
  const [loadingFolders, setLoadingFolders] = useState(false)
  const [loadingNestedFolders, setLoadingNestedFolders] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [needsTenantAuth, setNeedsTenantAuth] = useState(false)
  const [authInProgress, setAuthInProgress] = useState(false)

  const [transferId, setTransferId] = useState<string | null>(null)
  const [transferStatus, setTransferStatus] = useState<string | null>(null)
  const [progressSnackOpen, setProgressSnackOpen] = useState(false)
  const [resultSnackOpen, setResultSnackOpen] = useState(false)
  const [resultSnackError, setResultSnackError] = useState(false)
  const [resultSnackMsg, setResultSnackMsg] = useState('')

  const targetTenantTokenRef = useRef<string | null>(null)
  const sourceTenantAtSubmitRef = useRef<string | null>(null)
  const lastTenantReqRef = useRef<string | null>(null)
  const lastProjectReqRef = useRef<string | null>(null)
  const lastFolderReqRef = useRef<string | null>(null)
  const mountedRef = useRef(false)
  const pollTimerRef = useRef<number | null>(null)

  const authPopupRef = useRef<Window | null>(null)
  const authPollTimerRef = useRef<number | null>(null)
  const authTenantRef = useRef<string | null>(null)
  const storageListenerRef = useRef<((e: StorageEvent) => void) | undefined>(
    undefined
  )

  const availableTenants = useMemo(
    () =>
      tenants.filter(
        t =>
          t.id !== currentTenantId &&
          String(t.role || '').toLowerCase() === 'owner'
      ),
    [tenants, currentTenantId]
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stopTransferPolling()
      cleanupAuthListeners()
    }
  }, [])

  useEffect(() => {
    if (!open) return
    setSubmitting(false)
    setTenantId('')
    setProjectId('')
    setFolderId('')
    setNestedFolderId('')
    setProjects([])
    setFolders([])
    setNestedFolders([])
    setNeedsTenantAuth(false)
    setAuthInProgress(false)
    targetTenantTokenRef.current = null
    setTransferId(null)
    setTransferStatus(null)
    stopTransferPolling()
    setProgressSnackOpen(false)
    setResultSnackOpen(false)
    cleanupAuthListeners()

    if (tenantsFromStore?.length) {
      setTenants(tenantsFromStore)
    } else {
      setLoadingTenants(true)
      apiGetUserTenants()
        .then(d => mountedRef.current && setTenants(toItems(d)))
        .finally(() => mountedRef.current && setLoadingTenants(false))
    }
  }, [open, tenantsFromStore])

  const loadProjectsForTenant = useCallback(
    async (selectedTenantId: string) => {
      lastTenantReqRef.current = selectedTenantId
      setLoadingProjects(true)
      try {
        const token = targetTenantTokenRef.current
        const data = await withTimeout(
          apiListProjects(
            selectedTenantId !== currentTenantId && token
              ? { token }
              : undefined
          )
        )
        if (
          mountedRef.current &&
          lastTenantReqRef.current === selectedTenantId
        ) {
          setProjects(toItems(data))
        }
      } catch (err) {
        const status = httpStatusOf(err)
        if (
          selectedTenantId !== currentTenantId &&
          (status === 401 || status === 403)
        ) {
          clearTenantAccessToken(selectedTenantId)
          targetTenantTokenRef.current = null
          setProjects([])
          setNeedsTenantAuth(true)
        } else {
          setProjects([])
        }
      } finally {
        mountedRef.current && setLoadingProjects(false)
      }
    },
    [currentTenantId]
  )

  const loadFoldersForTenant = useCallback(
    async (selectedTenantId: string, selectedProjectId: string) => {
      lastProjectReqRef.current = selectedProjectId
      setLoadingFolders(true)
      try {
        const token = targetTenantTokenRef.current
        const data = await withTimeout(
          apiListFolders(
            selectedProjectId,
            selectedTenantId !== currentTenantId && token
              ? { token }
              : undefined
          )
        )
        if (
          mountedRef.current &&
          lastProjectReqRef.current === selectedProjectId
        ) {
          setFolders(toItems(data))
        }
      } catch (err) {
        const status = httpStatusOf(err)
        if (
          selectedTenantId !== currentTenantId &&
          (status === 401 || status === 403)
        ) {
          clearTenantAccessToken(selectedTenantId)
          targetTenantTokenRef.current = null
          setFolders([])
          setNeedsTenantAuth(true)
        } else {
          setFolders([])
        }
      } finally {
        mountedRef.current && setLoadingFolders(false)
      }
    },
    [currentTenantId]
  )

  const loadNestedFolders = useCallback(
    async (selectedTenantId: string, parentFolderId: string) => {
      lastFolderReqRef.current = parentFolderId
      setLoadingNestedFolders(true)
      try {
        const token = targetTenantTokenRef.current
        const data = await withTimeout(
          apiListFoldersRecursive(
            parentFolderId,
            selectedTenantId !== currentTenantId && token
              ? { token }
              : undefined
          )
        )
        if (mountedRef.current && lastFolderReqRef.current === parentFolderId) {
          setNestedFolders(toItems(data))
        }
      } catch (err) {
        const status = httpStatusOf(err)
        if (
          selectedTenantId !== currentTenantId &&
          (status === 401 || status === 403)
        ) {
          clearTenantAccessToken(selectedTenantId)
          targetTenantTokenRef.current = null
          setNestedFolders([])
          setNeedsTenantAuth(true)
        } else {
          console.error('Error loading nested folders:', err)
          setNestedFolders([])
        }
      } finally {
        mountedRef.current && setLoadingNestedFolders(false)
      }
    },
    [currentTenantId]
  )

  useEffect(() => {
    if (!tenantId) return
    setProjectId('')
    setFolderId('')
    setNestedFolderId('')
    setProjects([])
    setFolders([])
    setNestedFolders([])
    setNeedsTenantAuth(false)

    const run = async () => {
      if (tenantId === currentTenantId) {
        targetTenantTokenRef.current = null
        await loadProjectsForTenant(tenantId)
        return
      }
      const cached = getCachedTenantAccessToken(tenantId)
      if (!cached || !isTokenUsableForTenant(cached, tenantId)) {
        if (cached) clearTenantAccessToken(tenantId)
        targetTenantTokenRef.current = null
        if (tenantId !== currentTenantId) setNeedsTenantAuth(true)
        return
      }
      targetTenantTokenRef.current = cached
      await loadProjectsForTenant(tenantId)
    }

    void run()
  }, [tenantId, currentTenantId, loadProjectsForTenant])

  useEffect(() => {
    if (!tenantId || !projectId) return
    void loadFoldersForTenant(tenantId, projectId)
  }, [tenantId, projectId, loadFoldersForTenant])

  useEffect(() => {
    if (!tenantId || !folderId) return
    void loadNestedFolders(tenantId, folderId)
  }, [tenantId, folderId, loadNestedFolders])

  const cleanupAuthListeners = () => {
    window.removeEventListener('message', onAuthMessage as any)
    if (storageListenerRef.current) {
      window.removeEventListener('storage', storageListenerRef.current)
      storageListenerRef.current = undefined
    }
    if (authPollTimerRef.current) {
      window.clearInterval(authPollTimerRef.current)
      authPollTimerRef.current = null
    }
    if (authPopupRef.current && !authPopupRef.current.closed) {
      try {
        authPopupRef.current.close()
      } catch {
        // ignored
      }
    }
    authPopupRef.current = null
    authTenantRef.current = null
  }

  const finishAuthSuccess = async (tId: string, token: string) => {
    cacheTenantAccessToken(tId, token)
    targetTenantTokenRef.current = token
    setNeedsTenantAuth(false)
    setAuthInProgress(false)
    await loadProjectsForTenant(tId)
  }

  const onAuthMessage = useCallback(
    (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return
      const { type, tenantId: tId, token } = (e.data || {}) as {
        type?: string
        tenantId?: string
        token?: string
      }
      if (type !== 'tenant-auth-ok' || !tId || !token) return
      if (!authTenantRef.current || tId !== authTenantRef.current) return
      if (!isTokenUsableForTenant(token, tId)) {
        setNeedsTenantAuth(true)
        setAuthInProgress(false)
        cleanupAuthListeners()
        return
      }
      cleanupAuthListeners()
      void finishAuthSuccess(tId, token)
    },
    [loadProjectsForTenant]
  )

  const authorizeForTenant = () => {
    if (!tenantId) return
    authTenantRef.current = tenantId
    setAuthInProgress(true)

    window.addEventListener('message', onAuthMessage as any)

    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'mc_tenant_access_tokens') return
      const tId = authTenantRef.current
      if (!tId) return
      const tok = getCachedTenantAccessToken(tId)
      if (!tok) return
      cleanupAuthListeners()
      void finishAuthSuccess(tId, tok)
    }
    storageListenerRef.current = onStorage
    window.addEventListener('storage', onStorage)

    const w = window.open(
      `${window.location.origin}?tenantId=${tenantId}&postBack=1`,
      'tenant-auth',
      'width=520,height=640'
    )
    if (!w) {
      setAuthInProgress(false)
      cleanupAuthListeners()
      alert(
        'Please allow popups to authorize this site, then click "Authorize" again.'
      )
      return
    }
    authPopupRef.current = w

    authPollTimerRef.current = window.setInterval(() => {
      if (!authPopupRef.current || authPopupRef.current.closed) {
        setAuthInProgress(false)
        const tId = authTenantRef.current
        cleanupAuthListeners()
        if (tId) {
          const tok = getCachedTenantAccessToken(tId)
          if (tok) {
            void finishAuthSuccess(tId, tok)
          } else {
            setNeedsTenantAuth(true)
          }
        }
      }
    }, 600)
  }

  const stopTransferPolling = () => {
    if (pollTimerRef.current) {
      window.clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }

  const fetchTransferStatus = useCallback(
    async (id: string) => {
      const srcTenantId = sourceTenantAtSubmitRef.current || currentTenantId
      const srcToken = getCachedTenantAccessToken(srcTenantId)
      const hdr =
        srcToken && isTokenUsableForTenant(srcToken, srcTenantId)
          ? { token: srcToken }
          : undefined

      try {
        const data = await withTimeout(apiGetTransferStatus(id, hdr), 12000)
        return data
      } catch (e) {
        const status = httpStatusOf(e)
        if (status === 404) return { status: 'None' }
        throw e
      }
    },
    [currentTenantId]
  )

  const isSuccess = (s: string) => {
    const v = s?.toLowerCase?.() || ''
    return (
      v === 'succeeded' ||
      v === 'success' ||
      v === 'completed' ||
      v === 'complete' ||
      v === 'done'
    )
  }
  const isFailure = (s: string) => {
    const v = s?.toLowerCase?.() || ''
    return (
      v === 'failed' ||
      v === 'error' ||
      v === 'cancelled' ||
      v === 'canceled' ||
      v === 'aborted'
    )
  }

  const startTransferPolling = (id: string) => {
    setTransferId(id)
    setTransferStatus('Queued')
    setProgressSnackOpen(true)

    let ticks = 0
    const maxTicks = 240

    const checkOnce = async () => {
      try {
        const data: any = await fetchTransferStatus(id)
        const status = String(data?.status ?? 'Unknown')
        if (!mountedRef.current) return
        setTransferStatus(status)

        if (isSuccess(status)) {
          stopTransferPolling()
          setProgressSnackOpen(false)
          setResultSnackError(false)
          setResultSnackMsg('Copy completed successfully.')
          setResultSnackOpen(true)
          onCopied?.()
        } else if (isFailure(status)) {
          stopTransferPolling()
          setProgressSnackOpen(false)
          setResultSnackError(true)
          setResultSnackMsg(
            data?.errorMessage || 'Copy failed. Please try again.'
          )
          setResultSnackOpen(true)
        } else {
          ticks += 1
          if (ticks >= maxTicks) {
            stopTransferPolling()
            setProgressSnackOpen(false)
            setResultSnackError(false)
            setResultSnackMsg(
              'Copy is taking longer than expected and will continue in the background.'
            )
            setResultSnackOpen(true)
          }
        }
      } catch (e) {
        if (!mountedRef.current) return
        ticks += 1
        setTransferStatus('Checking status...')
        if (ticks >= maxTicks) {
          stopTransferPolling()
          setProgressSnackOpen(false)
          setResultSnackError(true)
          setResultSnackMsg(messageOf(e) || 'Unable to check copy status.')
          setResultSnackOpen(true)
        }
      }
    }

    void checkOnce()
    stopTransferPolling()
    pollTimerRef.current = window.setInterval(checkOnce, 5000)
  }

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      sourceTenantAtSubmitRef.current = currentTenantId
      const finalContainerId = nestedFolderId || folderId || projectId
      const job = await apiCopyDataset(datasetId, tenantId, finalContainerId)
      const jobId: string | undefined =
        job?.id || job?.data?.id || job?.Item?.id || job?.JobId
      if (!jobId) throw new Error('Copy started but no job id was returned.')

      setSubmitting(false)
      onClose()
      startTransferPolling(jobId)
    } catch (err) {
      const msg = messageOf(err) || 'Copy failed'
      setSubmitting(false)
      setResultSnackError(true)
      setResultSnackMsg(msg)
      setResultSnackOpen(true)
    }
  }

  useEffect(() => {
    if (!projectId) return
    setFolderId('')
    setNestedFolderId('')
    setFolders([])
    setNestedFolders([])
  }, [projectId])

  useEffect(() => {
    if (!folderId) return
    setNestedFolderId('')
    setNestedFolders([])
  }, [folderId])

  const projectsDisabled =
    !tenantId || needsTenantAuth || loadingProjects || submitting
  const foldersDisabled =
    !projectId || needsTenantAuth || loadingFolders || submitting
  const nestedFoldersDisabled =
    !folderId || needsTenantAuth || loadingNestedFolders || submitting
  const submitDisabled =
    !tenantId || !projectId || needsTenantAuth || submitting

  return (
    <>
      <Dialog
        open={open}
        onClose={() => !submitting && onClose()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Copy dataset to another site</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Select the target Site and (optionally) a folder. You must be an{' '}
            <b>Owner</b> on the target site to copy into it.
          </Typography>

          <TextField
            select
            label="Target site"
            value={tenantId}
            onChange={e => setTenantId(e.target.value as string)}
            margin="normal"
            fullWidth
            disabled={loadingTenants || submitting}
            InputLabelProps={{ shrink: true }}
            helperText={
              loadingTenants
                ? 'Loading sites...'
                : availableTenants.length === 0
                ? "No other sites where you're an Owner."
                : 'Only sites where you are an Owner are shown.'
            }
          >
            {availableTenants.map(t => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </TextField>

          {tenantId && tenantId !== currentTenantId && needsTenantAuth && (
            <StyledAuthNotice>
              <Typography variant="body2" style={{ flex: 1 }}>
                To list folders in <b>this site</b>, please authorize it. This
                opens a small window and won't leave this page.
              </Typography>
              <Button
                onClick={authorizeForTenant}
                size="small"
                variant="outlined"
                color="primary"
                disabled={authInProgress}
              >
                {authInProgress ? (
                  <>
                    {'Authorizing...'}{' '}
                    <CircularProgress size={16} sx={{ marginLeft: 1 }} />
                  </>
                ) : (
                  'Authorize'
                )}
              </Button>
            </StyledAuthNotice>
          )}

          <TextField
            select
            label="Target folder"
            value={projectId}
            onChange={e => setProjectId(e.target.value as string)}
            margin="normal"
            fullWidth
            disabled={projectsDisabled}
            InputLabelProps={{ shrink: true }}
            helperText={
              !tenantId
                ? 'Select a site first'
                : needsTenantAuth
                ? 'Authorize the site to list projects'
                : loadingProjects
                ? 'Loading folders...'
                : projects.length === 0
                ? 'No folders found in this site'
                : ''
            }
          >
            {projects.map(p => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Target subfolder (optional)"
            value={folderId}
            onChange={e => setFolderId(e.target.value as string)}
            margin="normal"
            fullWidth
            disabled={foldersDisabled}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (val: unknown) => {
                const v = String(val ?? '')
                if (!v) return <span style={{ opacity: 0.6 }}>None</span>
                const f = folders.find(x => x.id === v)
                return f ? f.name : v
              }
            }}
            helperText={
              !projectId
                ? 'Select a folder first'
                : loadingFolders
                ? 'Loading subfolders...'
                : 'Leave empty to copy into the root folder'
            }
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {folders.map(f => (
              <MenuItem key={f.id} value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </TextField>

          {folderId && (
            <TextField
              select
              label="Target nested folder (optional)"
              value={nestedFolderId}
              onChange={e => setNestedFolderId(e.target.value as string)}
              margin="normal"
              fullWidth
              disabled={nestedFoldersDisabled}
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (val: unknown) => {
                  const v = String(val ?? '')
                  if (!v) return <span style={{ opacity: 0.6 }}>None</span>
                  const f = nestedFolders.find(x => x.id === v)
                  return f ? f.name : v
                }
              }}
              helperText={
                loadingNestedFolders
                  ? 'Loading nested folders...'
                  : nestedFolders.length === 0
                  ? 'No nested folders found'
                  : `Found ${nestedFolders.length} nested folder(s)`
              }
            >
              <MenuItem value="">
                <em>None - Copy to selected subfolder</em>
              </MenuItem>
              {nestedFolders.map(f => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmit}
            disabled={submitDisabled}
          >
            {submitting ? <CircularProgress size={20} /> : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={progressSnackOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <StyledSnackInfo
          message={
            <StyledSnackMsg>
              <CircularProgress size={16} style={{ color: '#FFF' }} />
              Copy in progress...
            </StyledSnackMsg>
          }
        />
      </Snackbar>

      <Snackbar
        open={resultSnackOpen}
        autoHideDuration={4000}
        onClose={() => setResultSnackOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {resultSnackError ? (
          <StyledSnackError
            message={<StyledSnackMsg>{resultSnackMsg}</StyledSnackMsg>}
          />
        ) : (
          <StyledSnackSuccess
            message={<StyledSnackMsg>{resultSnackMsg}</StyledSnackMsg>}
          />
        )}
      </Snackbar>
    </>
  )
}
